import { useEffect, useState } from 'react'
import { Check, Clipboard, RefreshCw, Send, ShieldCheck } from 'lucide-react'
import QRCode from 'react-qr-code'

import { api, jsonBody, setCsrfToken } from '../api'
import type { Locale } from '../i18n'
import type { AuthState } from '../types'
import { AuthLayout } from './AuthLayout'

type Methods = { telegram: { enabled: boolean; username: string | null }; legacy: boolean }
type Attempt = { attemptId: string; browserSecret: string; deepLink: string; expiresAt: string }
type Phase = 'loading' | 'ready' | 'starting' | 'waiting' | 'expired' | 'unavailable' | 'failed'

export function TelegramAuthPage({ locale, setLocale, go }: { locale: Locale; setLocale: (locale: Locale) => void; go: (path: string) => void }) {
  const [methods, setMethods] = useState<Methods | null>(null)
  const [attempt, setAttempt] = useState<Attempt | null>(null)
  const [phase, setPhase] = useState<Phase>('loading')
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    api<Methods>('/api/auth/methods').then(value => {
      if (!active) return
      setMethods(value); setPhase(value.telegram.enabled ? 'ready' : 'unavailable')
    }).catch(caught => { if (active) { setError(caught instanceof Error ? caught.message : 'Request failed.'); setPhase('unavailable') } })
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (phase !== 'waiting' || !attempt) return
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    let active = true
    const poll = async () => {
      if (!active) return
      if (Date.parse(attempt.expiresAt) <= Date.now()) { setPhase('expired'); return }
      try {
        const response = await fetch(`/api/auth/telegram/attempts/${encodeURIComponent(attempt.attemptId)}`, {
          credentials: 'same-origin', signal: controller.signal, headers: { authorization: `Bearer ${attempt.browserSecret}` }
        })
        const body = await response.json().catch(() => ({})) as Partial<AuthState> & { state?: string; error?: { message?: string } }
        if (response.status === 202) { timer = setTimeout(poll, 1_500); return }
        if (response.status === 429) {
          const seconds = Number(response.headers.get('retry-after'))
          const delay = Number.isFinite(seconds) && seconds >= 0 ? Math.max(250, Math.min(10_000, seconds * 1_000)) : 3_000
          timer = setTimeout(poll, delay)
          return
        }
        if (response.ok && body.state === 'authenticated' && body.csrfToken && body.user) { setCsrfToken(body.csrfToken); go(body.user.role === 'admin' ? '/app/admin' : '/app'); return }
        if (response.status === 410) { setPhase('expired'); return }
        throw new Error(body.error?.message || `Request failed (${response.status})`)
      } catch (caught) {
        if (!active || controller.signal.aborted) return
        setError(caught instanceof Error ? caught.message : 'Request failed.'); setPhase('failed')
      }
    }
    timer = setTimeout(poll, 0)
    return () => { active = false; controller.abort(); if (timer) clearTimeout(timer) }
  }, [attempt, go, phase])

  const start = async () => {
    setPhase('starting'); setError('')
    try {
      const value = await api<Attempt>('/api/auth/telegram/start', { method: 'POST', body: jsonBody({}) })
      setAttempt(value); setPhase('waiting')
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'Request failed.'); setPhase('failed') }
  }
  const restart = () => { setAttempt(null); setError(''); setPhase(methods?.telegram.enabled ? 'ready' : 'unavailable') }
  const unavailable = locale === 'en' ? 'Telegram sign in is temporarily unavailable.' : 'Telegram 登录暂时不可用。'

  return <AuthLayout locale={locale} setLocale={setLocale} go={go}>
    <section className="auth-form telegram-auth" aria-live="polite">
      <span className="auth-symbol telegram-symbol"><Send /></span>
      <h1>{locale === 'en' ? 'Sign in with Telegram' : '使用 Telegram 登录'}</h1>
      <p>{locale === 'en' ? 'Secure access without email or passwords.' : '无需邮箱或密码的安全访问。'}</p>
      {phase === 'unavailable' && <div className="alert alert-error" role="alert">{error || unavailable}</div>}
      {phase === 'failed' && <div className="alert alert-error" role="alert">{error || (locale === 'en' ? 'Telegram sign in failed.' : 'Telegram 登录失败。')}</div>}
      {phase === 'expired' && <div className="alert alert-error" role="alert">{locale === 'en' ? 'This login request expired.' : '本次登录请求已过期。'}</div>}
      {phase === 'waiting' && attempt ? <div className="telegram-waiting">
        <div className="telegram-qr" role="img" aria-label="Telegram login QR code"><QRCode value={attempt.deepLink} size={172} /></div>
        <div className="telegram-status"><span className="live-dot" /><strong>{locale === 'en' ? 'Waiting for Telegram confirmation' : '等待 Telegram 确认'}</strong></div>
        <a className="button button-primary button-submit" href={attempt.deepLink} target="_blank" rel="noreferrer"><Send size={18} />{locale === 'en' ? 'Open Telegram' : '打开 Telegram'}</a>
        <button type="button" className="button button-outline button-submit" title={locale === 'en' ? 'Copy Telegram link' : '复制 Telegram 链接'} onClick={() => navigator.clipboard.writeText(attempt.deepLink)}><Clipboard size={18} />{locale === 'en' ? 'Copy link' : '复制链接'}</button>
      </div> : <button type="button" className="button button-primary button-submit" disabled={!methods?.telegram.enabled || phase === 'loading' || phase === 'starting'} onClick={start}><Send size={18} />{phase === 'starting' || phase === 'loading' ? (locale === 'en' ? 'Preparing...' : '准备中...') : (locale === 'en' ? 'Continue with Telegram' : '使用 Telegram 继续')}</button>}
      {(phase === 'expired' || phase === 'failed') && <button type="button" className="button button-outline button-submit telegram-retry" onClick={restart}><RefreshCw size={18} />{locale === 'en' ? 'Try again' : '重试'}</button>}
      <div className="telegram-trust"><ShieldCheck size={18} /><span>{locale === 'en' ? 'One-time, encrypted, and replay protected' : '一次性、加密并防止重放'}</span>{phase === 'waiting' && <Check size={17} />}</div>
      <p className="auth-switch"><a href="/login/password" onClick={event => { event.preventDefault(); go('/login/password') }}>{locale === 'en' ? 'Sign in with password' : '账号密码登录'}</a></p>
    </section>
  </AuthLayout>
}
